import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2Icon, LockIcon, MailCheckIcon, MailIcon, UserIcon } from 'lucide-react';
import {
  AuthShell,
  BrandMark,
  Button,
  Callout,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconInput,
  PasswordInput,
  ResultState,
} from '@opengoal/ui';
import { useTranslation } from 'react-i18next';
import { agenciesControllerSignUpV1 } from '@/api/generated/agencies/agencies';
import { env } from '@/app/env';
import { errorMessage } from '@/shared/lib/errors';
import { applyFieldErrors } from '@/shared/lib/formErrors';

/**
 * Public self-signup: one form creates the agency and its owner. No session
 * comes back — the address has to be confirmed first, so the success state
 * is "check your inbox", and the emailed link lands on /verify-email which
 * signs the user in and sends them to choose a plan.
 */
export function SignUpPage() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const schema = z.object({
    agencyName: z.string().trim().min(1, t('auth.agencyNameRequired')).max(100),
    name: z.string().trim().min(1, t('auth.nameRequired')).max(100),
    email: z.string().trim().min(1, t('auth.emailRequired')).email(t('auth.emailInvalid')),
    password: z.string().min(1, t('auth.choosePassword')).min(8, t('auth.passwordTooShort')),
  });
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { agencyName: '', name: '', email: '', password: '' },
  });

  const signUp = useMutation({
    mutationFn: (values: Values) => agenciesControllerSignUpV1(values),
    onSuccess: (result) => setSentTo(result.user.email),
    onError: (failure: unknown) => {
      setError(
        applyFieldErrors(form, failure, ['agencyName', 'name', 'email', 'password']) ??
          errorMessage(failure),
      );
    },
  });

  const brand = <BrandMark name={env.appName} tagline={t('app.tagline')} />;

  if (sentTo) {
    return (
      <AuthShell brand={brand} title={t('auth.signUpSentTitle')}>
        <ResultState
          tone="success"
          icon={<MailCheckIcon />}
          description={t('auth.signUpSentBody', { email: sentTo })}
          className="px-0 py-2"
          action={
            <Button asChild variant="outline">
              <Link to="/login">{t('auth.backToSignIn')}</Link>
            </Button>
          }
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      brand={brand}
      title={t('auth.signUpTitle')}
      subtitle={t('auth.signUpSubtitle')}
      aside={{ heading: t('auth.signUpAsideHeading'), body: t('auth.signUpAsideBody') }}
      footer={
        <span className="text-muted-foreground">
          {t('auth.haveAccount')}{' '}
          <Link to="/login" className="font-medium text-foreground hover:underline">
            {t('auth.signIn')}
          </Link>
        </span>
      }
    >
      <Form {...form}>
        <form
          noValidate
          className="grid gap-5"
          onSubmit={form.handleSubmit((values) => {
            setError(null);
            signUp.mutate(values);
          })}
        >
          <FormField
            control={form.control}
            name="agencyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.agencyName')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<Building2Icon />}
                    autoComplete="organization"
                    placeholder={t('auth.agencyNamePlaceholder')}
                    className="h-10"
                    autoFocus
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.fullName')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<UserIcon />}
                    autoComplete="name"
                    placeholder={t('auth.fullNamePlaceholder')}
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')}</FormLabel>
                <FormControl>
                  <IconInput
                    icon={<MailIcon />}
                    type="email"
                    autoComplete="email"
                    placeholder="you@agency.com"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    icon={<LockIcon />}
                    autoComplete="new-password"
                    className="h-10"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {error ? <Callout variant="destructive" title={error} /> : null}

          <Button type="submit" size="lg" className="w-full" loading={signUp.isPending}>
            {t('auth.createAgency')}
          </Button>
          <p className="text-center text-xs text-muted-foreground">{t('auth.signUpTerms')}</p>
        </form>
      </Form>
    </AuthShell>
  );
}
